import { z } from 'zod';

// ClientIdSchema is exported for use with branded ID parameters.
// The id field in ClientSchema itself is unbranded z.number() to match
// the drizzle-zod selectClientSchema output (bigint mode:'number' → z.number()).
export const ClientIdSchema = z.number().brand<'ClientId'>();
export type ClientId = z.infer<typeof ClientIdSchema>;

// Deliberately generous relative to real client acronyms (2-4 chars in every
// documented example, e.g. VM, ARC, VALM): a campaign number without a brand
// element — ordinal/month/year/acronym/initials — spends at most ~16 chars on
// everything except the acronym even at a 4-digit ordinal, leaving ample
// headroom under the 64-char campaign-number edit-path limit
// (UpdateCampaignNumberSchema) while never truncating the acronym itself.
export const CLIENT_ACRONYM_MAX_LENGTH = 20;

const ClientBaseSchema = z.object({
  id: z.number(),
  name: z.string(),
  // Uniqueness (case-insensitive, among non-archived clients) is enforced by
  // the storage layer's partial unique index, not here. `.trim()`/`.min()`/
  // `.max()`/`.refine()` all stay ZodString in this Zod version, so this
  // matches drizzle-zod's plain `z.string()` for clients-drift.test.ts.
  acronym: z
    .string()
    .trim()
    .min(1)
    .max(CLIENT_ACRONYM_MAX_LENGTH)
    .refine((value) => !value.includes('/'), {
      message: "Acronym must not contain '/', the campaign-number separator",
    }),
  notes: z.string().nullable(),
  brands: z.array(z.string().trim().min(1)),
  createdBy: z.number(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

const ClientDateFields = {
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable(),
};

export const ClientSchema = ClientBaseSchema.extend(ClientDateFields);
export type Client = z.infer<typeof ClientSchema>;

export const CreateClientSchema = ClientSchema.omit({
  id: true,
  createdBy: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
  .extend({
    // Optional on create: when omitted, the server suggests one from the
    // name (client-entity capability, task 2.2).
    acronym: ClientSchema.shape.acronym.optional(),
    brands: ClientSchema.shape.brands.optional(),
    notes: ClientSchema.shape.notes.optional(),
  })
  .strict();
export type CreateClient = z.infer<typeof CreateClientSchema>;

export const UpdateClientSchema = CreateClientSchema.partial().strict();
export type UpdateClient = z.infer<typeof UpdateClientSchema>;

export const ListClientsQuerySchema = z
  .object({
    q: z.string().trim().min(1).optional(),
  })
  .strict();
export type ListClientsQuery = z.infer<typeof ListClientsQuerySchema>;
